import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Input,
  Table,
} from 'reactstrap';
import notify, { MESSAGES } from '../../../utils/notify';
import { confirmAction } from '../../../utils/confirm';
import ActionButton from '../../../Components/Common/ActionButton';
import { EmptyState } from '../../../Components/Common/ListStates';
import { SkeletonTable } from '../../../Components/Common/Skeleton';

import { api } from '../../../config';
import { RIGHTS_FIELDS } from '../constants';
import {
  buildRightsToastMessage,
  getMenuName,
  hasRightsChanged,
  normalizeMenuRoleItem,
} from '../utils/menuRoleUtils';

const RightsModal = ({
  isOpen,
  toggle,
  token,
  selectedRole,
  menuOptions,
  onAssign,
}) => {
  const [modalLoading, setModalLoading] = useState(false);
  const [rights, setRights] = useState([]);
  const [rightsLoaded, setRightsLoaded] = useState(false);
  const [originalRights, setOriginalRights] = useState([]);
  const [searchText, setSearchText] = useState('');

  const resetState = () => {
    setRights([]);
    setRightsLoaded(false);
    setOriginalRights([]);
    setSearchText('');
  };

  useEffect(() => {
    if (!isOpen || !selectedRole || !token) return;

    const loadRights = async () => {
      resetState();

      try {
        const response = await fetch(
          `${api.API_URL}/menu-role/by-role/${selectedRole.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error('Failed to load menu rights');

        const result = await response.json();
        const rawData = result.data?.items ?? result.data;
        const existingItems = Array.isArray(rawData) ? rawData : [];
        const normalized = existingItems
          .filter(item => item && item.menuId != null)
          .map(normalizeMenuRoleItem);

        setRights(normalized);
        setRightsLoaded(true);
        setOriginalRights(normalized.map(item => ({ ...item })));
      } catch (error) {
        notify.apiError(error, 'load menu rights');
        toggle();
      }
    };

    loadRights();
  }, [isOpen, selectedRole, token]);

  const hasChanges = useMemo(() => {
    return rights.some(item => {
      const original = originalRights.find(o => o.id === item.id);
      return original && hasRightsChanged(item, original);
    });
  }, [rights, originalRights]);

  const filteredRights = useMemo(() => {
    const query = searchText.toLowerCase().trim();
    if (!query) return rights;

    return rights.filter(item => {
      const menuName = getMenuName(menuOptions, item.menuId).toLowerCase();
      return menuName.includes(query);
    });
  }, [rights, searchText, menuOptions]);

  const handleClose = () => {
    resetState();
    toggle();
  };

  // User-initiated dismiss (X, backdrop, Cancel) asks before dropping edits.
  const requestClose = async () => {
    if (hasChanges && !modalLoading) {
      const discard = await confirmAction({
        tone: 'warning',
        title: 'Discard changes?',
        message: 'Your access rights changes have not been saved.',
        subject: selectedRole?.name,
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
      });
      if (!discard) return;
    }
    handleClose();
  };

  const handleToggle = (mappingId, field) => {
    setRights(prev =>
      prev.map(item =>
        item.id === mappingId ? { ...item, [field]: !item[field] } : item
      )
    );
  };

  const handleToggleColumn = (field, value) => {
    const visibleIds = new Set(filteredRights.map(item => item.id));
    setRights(prev =>
      prev.map(item =>
        visibleIds.has(item.id) ? { ...item, [field]: value } : item
      )
    );
  };

  const isColumnChecked = field => {
    if (filteredRights.length === 0) return false;
    return filteredRights.every(item => item[field]);
  };

  const isColumnIndeterminate = field => {
    if (filteredRights.length === 0) return false;
    const checkedCount = filteredRights.filter(item => item[field]).length;
    return checkedCount > 0 && checkedCount < filteredRights.length;
  };

  const handleSave = async () => {
    if (!hasChanges) {
      notify.info(MESSAGES.noChanges);
      handleClose();
      return;
    }

    setModalLoading(true);
    try {
      const changedItems = rights.filter(item => {
        const original = originalRights.find(o => o.id === item.id);
        return original && hasRightsChanged(item, original);
      });

      const updatePromises = changedItems.map(item =>
        // fetch(`${api.API_URL}/menu-role/${item.id}`, {
        //   method: 'POST',
        //   headers: {
        //     'Content-Type': 'application/json',
        //     Authorization: `Bearer ${token}`,
        //   },
        //   body: JSON.stringify({
        //     roleId: selectedRole.id,
        //     menuId: item.menuId,
        //     canCreate: item.canCreate,
        //     canRead: item.canRead,
        //     canUpdate: item.canUpdate,
        //     canDelete: item.canDelete,
        //   }),
        // })
        fetch(`${api.API_URL}/menu-role/bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            menuId: item.menuId,
            roles: [
              {
                roleId: selectedRole.id,
                canCreate: item.canCreate,
                canRead: item.canRead,
                canUpdate: item.canUpdate,
                canDelete: item.canDelete,
              },
            ],
          }),
        })
          .then(res => {
            if (!res.ok) {
              throw new Error(
                `Failed to update rights for ${getMenuName(menuOptions, item.menuId)}`
              );
            }
            return { menuId: item.menuId };
          })
          .catch(err => {
            throw { menuId: item.menuId, message: err.message };
          })
      );

      const results = await Promise.allSettled(updatePromises);
      const enrichedResults = results.map((res, idx) => ({
        ...res,
        menuId: changedItems[idx]?.menuId,
      }));

      const totalFailed = enrichedResults.filter(
        r => r.status === 'rejected'
      ).length;
      const message = buildRightsToastMessage(menuOptions, enrichedResults);

      if (totalFailed === 0) {
        notify.success(message);
        handleClose();
      } else {
        notify.warning(message);

        const successIds = enrichedResults
          .filter(r => r.status === 'fulfilled')
          .map((_, idx) => changedItems[idx].id);

        setOriginalRights(prev =>
          prev.map(item => {
            if (!successIds.includes(item.id)) return item;
            const updated = rights.find(r => r.id === item.id);
            return updated ? { ...updated } : item;
          })
        );
      }
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={requestClose}
      size='xl'
      backdrop='static'
      centered
    >
      <ModalHeader toggle={requestClose} className='bg-light'>
        Menu Rights for {selectedRole?.name || selectedRole?.id}
      </ModalHeader>
      <ModalBody>
        <div className='search-box mb-3'>
          <Input
            type='text'
            className='form-control'
            placeholder='Search assigned menu...'
            aria-label='Search assigned menu'
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <i className='ri-search-line search-icon' aria-hidden='true'></i>
        </div>

        {!rightsLoaded ? (
          <SkeletonTable rows={5} columns={5} />
        ) : rights.length === 0 ? (
          <EmptyState
            compact
            icon='ri-shield-keyhole-line'
            title='No menus assigned to this role'
            emptyHint='Rights can only be set for menus that are assigned to the role. Assign menus first.'
            action={
              onAssign
                ? {
                    label: 'Assign menus',
                    icon: 'ri-links-line',
                    onClick: onAssign,
                  }
                : null
            }
          />
        ) : (
          <div className='table-responsive'>
            <Table
              bordered
              hover
              className='align-middle mb-0'
              aria-label='Menu rights'
            >
              <thead className='table-light'>
                <tr>
                  <th style={{ minWidth: '160px' }}>Menu</th>
                  {RIGHTS_FIELDS.map(field => (
                    <th
                      key={field.key}
                      className='text-center'
                      style={{ width: '100px' }}
                    >
                      <div className='d-flex flex-column align-items-center gap-1'>
                        <span>{field.label}</span>
                        <Input
                          type='checkbox'
                          className='form-check-input'
                          checked={isColumnChecked(field.key)}
                          aria-label={`${field.label} for all menus`}
                          ref={el => {
                            if (el) {
                              el.indeterminate = isColumnIndeterminate(
                                field.key
                              );
                            }
                          }}
                          onChange={e =>
                            handleToggleColumn(field.key, e.target.checked)
                          }
                          disabled={filteredRights.length === 0}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRights.length === 0 ? (
                  <tr>
                    <td colSpan={5} className='text-center text-muted py-4'>
                      No menus match your search.
                    </td>
                  </tr>
                ) : (
                  filteredRights.map(item => (
                    <tr key={item.id}>
                      <td>{getMenuName(menuOptions, item.menuId)}</td>
                      {RIGHTS_FIELDS.map(field => (
                        <td key={field.key} className='text-center'>
                          <Input
                            type='checkbox'
                            className='form-check-input'
                            checked={item[field.key]}
                            aria-label={`${getMenuName(menuOptions, item.menuId)} ${field.label}`}
                            onChange={() => handleToggle(item.id, field.key)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <ActionButton
          variant='secondary'
          icon='ri-close-line'
          onClick={requestClose}
          disabled={modalLoading}
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant='primary'
          icon='ri-save-line'
          onClick={handleSave}
          loading={modalLoading}
          disabled={!hasChanges || rights.length === 0}
        >
          Save Rights
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default RightsModal;
